import React, { useMemo } from "react";
import { motion } from "motion/react";
import {
  Alert,
  Button,
  LockIcon,
  PageMessage,
  Separator,
  Typography,
} from "@picklebet/ui";
import { List, ListItem } from "@/features/shell/components/shared/List/List";
import { VerificationBadge } from "./VerificationBadge";
import { useVerificationPlan } from "../../hooks/useVerificationPlan";
import { RegionRestricted } from "./RegionRestricted";
import { PendingReview } from "./PendingReview";
import { ReactComponent as PendingIcon } from "assets/illustrations/warning-pending.svg";
import * as support from "@/utilities/support";
import {
  IoMVerificationOptionSkeleton,
  VerificationOptionSkeleton,
} from "./VerificationOptionSkeleton";
import {
  useCustomerLicence,
  useIsProfileIomRegulatedMarket,
} from "@/hooks/firestore/user/useProfile";
import { CustomerLicenceOptions } from "@/features/auth/stores/authStore";
import { verificationDataSourcePathMap } from "../../utils/verification";
import {
  VERIFICATION_DATA_SOURCES,
  type VerificationDataSource,
} from "features/account/types/Verification";
import type { UserVerificationAttemptDataSource } from "@/hooks/firestore/models/userVerifications";
import type { RouteComponentProps } from "@gatsbyjs/reach-router";
import { Sumsub } from "./sumsub/Sumsub";
import { VerificationError } from "./VerificationError";
import { SupportLink } from "@/features/shell/components/shared/SupportLink";
import * as styles from "./Verification.module.scss";
import pluralize from "pluralize";

type VerificationOptionsProps = {
  prefix?: string;
} & RouteComponentProps;

const DataSources = ({
  prefix,
  dataSources,
}: {
  prefix: string;
  dataSources: [string, UserVerificationAttemptDataSource][];
}) => {
  return dataSources.map(([key, dataSource]) => {
    const isVerified = dataSource.status !== "UNVERIFIED";
    const to =
      verificationDataSourcePathMap[
        key as keyof typeof verificationDataSourcePathMap
      ];

    return (
      <ListItem
        key={key}
        label={
          <>
            {dataSource.name}
            {isVerified && <LockIcon className={styles.lockIcon} />}
          </>
        }
        disabled={isVerified}
        to={!isVerified ? `${prefix}/verification/${to}` : undefined}
      >
        <VerificationBadge status={dataSource.status} />
      </ListItem>
    );
  });
};

/*
 * New verification options component to support our integration with SumSub.
 */
const VerificationOptions = ({
  prefix = "/account",
}: VerificationOptionsProps) => {
  const licence = useCustomerLicence();
  const isIOM = licence === CustomerLicenceOptions.ISLE_OF_MAN;
  const { isProfileIomRegulatedMarket } = useIsProfileIomRegulatedMarket();
  const {
    selectedAttempt,
    isLoading,
    isRetrying,
    verificationPathReady,
    hasNoPaths,
    error,
    retryPlan,
  } = useVerificationPlan();

  const dataSources = useMemo(() => {
    if (!selectedAttempt?.dataSources) return [];

    // If we're not using VIX documents we don't need to order them
    if (selectedAttempt.attemptType !== "VIXIdentityDocuments") {
      return Object.entries(selectedAttempt.dataSources);
    }

    const dataSourceOrder = Object.values(VERIFICATION_DATA_SOURCES);

    return Object.entries(selectedAttempt.dataSources).sort(
      ([dataSourceAKey], [dataSourceBKey]) => {
        return (
          dataSourceOrder.indexOf(dataSourceAKey as VerificationDataSource) -
          dataSourceOrder.indexOf(dataSourceBKey as VerificationDataSource)
        );
      },
    );
  }, [selectedAttempt?.dataSources]);

  // Users who land here straight after sign-up can outrun the verification data
  // being published to Firestore, usually seen if our jobs server is running
  // behind substantially. It will poll the endpoint for up to 60 seconds while
  // we wait for data, after that we display the error
  if (isRetrying || !verificationPathReady) {
    return (
      <RegionRestricted>
        <PageMessage
          title="Reviewing your details..."
          image={<PendingIcon />}
          subTitle={
            <Typography
              className={styles.pageMessageText}
              variant={`body`}
              noSpacing
            >
              This will only take a moment, hold tight.
            </Typography>
          }
        />
      </RegionRestricted>
    );
  }

  if (error) {
    const isPlanError = !(error instanceof Error) && error.source === "plan";
    return (
      <RegionRestricted>
        <VerificationError
          error={error}
          variant="page"
          actions={
            <Button
              onClick={isPlanError ? retryPlan : () => window.location.reload()}
              size="md"
              variant="danger"
            >
              {isPlanError ? "Try again" : "Refresh"}
            </Button>
          }
        />
      </RegionRestricted>
    );
  }

  const maxSourcesRequired = selectedAttempt?.maxSourcesRequired;

  return (
    <RegionRestricted>
      <PendingReview>
        <Typography variant={`h4`} noSpacing skeleton={{ loading: isLoading }}>
          Verify Your Identity
        </Typography>

        <Typography variant={`body`} skeleton={{ loading: isLoading }}>
          {selectedAttempt ? (
            isProfileIomRegulatedMarket ? (
              <>
                We are required to verify your identity to unlock higher
                transaction limits. Complete ID Verification to unlock increased
                deposit limits and withdrawals.{" "}
                <SupportLink
                  hideChevron
                  options={{ labels: ["weblink-kyc-verification"] }}
                >
                  Learn more
                </SupportLink>
                .
              </>
            ) : (
              <>
                Please submit{" "}
                {typeof maxSourcesRequired === "number" ? (
                  <em>
                    {maxSourcesRequired} {pluralize("form", maxSourcesRequired)}
                  </em>
                ) : (
                  "forms"
                )}{" "}
                of ID to complete verification.
              </>
            )
          ) : null}
        </Typography>

        {hasNoPaths ? (
          <Alert
            color="danger"
            actions={
              <Button
                onClick={() => support.openWithChat()}
                size="md"
                variant="danger"
              >
                Contact Support
              </Button>
            }
          >
            You are unable to perform self-verification. Please contact Support
            for assistance.
          </Alert>
        ) : isLoading || !selectedAttempt ? (
          isIOM ? (
            <IoMVerificationOptionSkeleton />
          ) : (
            <List loading>
              <VerificationOptionSkeleton count={6} />
            </List>
          )
        ) : selectedAttempt.attemptType === "VIXIdentityDocuments" ? (
          <List loading={isLoading}>
            <DataSources prefix={prefix} dataSources={dataSources} />
          </List>
        ) : (
          <Sumsub />
        )}

        {!isLoading &&
          selectedAttempt?.attemptType === "VIXIdentityDocuments" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Separator />
              <Alert
                color={`info`}
                actions={
                  <Button
                    onClick={() =>
                      support.open({ labels: ["weblink-verify-my-identity"] })
                    }
                    size={`md`}
                    variant={`info`}
                  >
                    Learn More
                  </Button>
                }
              >
                None of the above options available? See the other forms of ID
                you can submit.
              </Alert>
            </motion.div>
          )}
      </PendingReview>
    </RegionRestricted>
  );
};

export { VerificationOptions };
